import React, { useEffect, useRef, useState } from "react";

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/* ============================================================
   MediaGrid

   Layout is driven by each file's REAL aspect ratio, so tiles
   fit their media exactly: no empty space around an image and
   (almost) no cropping.

     1 → shown at its natural size
     2 → one row, widths proportional to each ratio
     3 → big tile on the left, two stacked on the right
     4 → two rows of two (each row sized to its own ratios)

   Props
     media           [{ type: "image" | "video", url, width?, height? }]
     onClick(m)      tap on a tile (zoom view)
     renderOverlay   (index) => node   optional, drawn inside each tile
                     (the composer uses it for upload progress bars)
============================================================ */
export default function MediaGrid({ media = [], onClick, renderOverlay }) {
  /* real aspect ratios, learned as each file loads (keyed by url) */
  const [ratios, setRatios] = useState({});

  const count = media.length;

  const ratioOf = (m) => {
    if (ratios[m.url]) return ratios[m.url];
    if (m.width && m.height) return m.width / m.height;
    return 1; // until the file reports its size
  };

  const setRatio = (m, r) => {
    if (!r || !Number.isFinite(r)) return;
    setRatios((prev) =>
      prev[m.url] && Math.abs(prev[m.url] - r) < 0.001
        ? prev
        : { ...prev, [m.url]: r }
    );
  };

  if (!count) return null;

  const handleClick = (e, m) => {
    e.stopPropagation();
    onClick?.(m);
  };

  /* ========================================
     🟦 SINGLE MEDIA (natural size, never cropped)
  ======================================== */

  if (count === 1) {
    const m = media[0];

    return (
      <div className="mt-3 rounded-2xl overflow-hidden">
        <div className="relative w-full max-w-[420px] md:max-w-[480px] mx-auto">
          {m.type === "video" ? (
            <AutoVideo
              src={m.url}
              className="w-full h-auto cursor-pointer rounded-2xl"
              onClick={(e) => handleClick(e, m)}
              iconClass="w-16 h-16 text-4xl"
            />
          ) : (
            <img
              src={m.url}
              alt=""
              className="w-full h-auto object-contain cursor-pointer rounded-2xl"
              onClick={(e) => handleClick(e, m)}
            />
          )}
          {renderOverlay?.(0)}
        </div>
      </div>
    );
  }

  /* ========================================
     🟦 MULTI MEDIA (2 / 3 / 4)
  ======================================== */

  const items = media.slice(0, 4);
  const r = items.map(ratioOf);

  const frame =
    "mt-3 w-full rounded-2xl overflow-hidden border " +
    "border-neutral-200 dark:border-neutral-700 " +
    "bg-neutral-200 dark:bg-neutral-700";

  const tile = (i, style) => (
    <MediaItem
      key={i}
      media={items[i]}
      index={i}
      onClick={onClick}
      onRatio={setRatio}
      renderOverlay={renderOverlay}
      style={style}
    />
  );

  /* a row of tiles: widths ∝ ratio, so they all share one height */
  const row = (idxs) => {
    const sum = idxs.reduce((a, i) => a + r[i], 0);
    return (
      <div
        key={idxs.join("-")}
        className="flex gap-0.5 w-full"
        style={{ aspectRatio: clamp(sum, 1.2, 3) }}
      >
        {idxs.map((i) => tile(i, { flex: `${r[i]} 1 0%` }))}
      </div>
    );
  };

  /* 2 → one row */
  if (items.length === 2) {
    return <div className={frame}>{row([0, 1])}</div>;
  }

  /* 3 → tall tile left, two stacked right */
  if (items.length === 3) {
    const k = 1 / r[1] + 1 / r[2]; // right column height in "widths"
    const rightW = 1 / k; // right column width share
    const aspect = clamp(r[0] + rightW, 0.8, 2.2);

    return (
      <div className={`${frame} flex gap-0.5`} style={{ aspectRatio: aspect }}>
        {tile(0, { flex: `${r[0]} 1 0%` })}

        <div
          className="flex flex-col gap-0.5 min-w-0 min-h-0"
          style={{ flex: `${rightW} 1 0%` }}
        >
          {tile(1, { flex: `${1 / r[1]} 1 0%` })}
          {tile(2, { flex: `${1 / r[2]} 1 0%` })}
        </div>
      </div>
    );
  }

  /* 4 → two rows of two */
  return (
    <div className={`${frame} flex flex-col gap-0.5`}>
      {row([0, 1])}
      {row([2, 3])}
    </div>
  );
}

/* ========================================
   MEDIA ITEM
   Fills the grid cell it is given. Cells are sized from the
   files' own ratios, so object-cover leaves no empty space.
======================================== */

function MediaItem({
  media,
  index,
  onClick,
  onRatio,
  renderOverlay,
  style,
}) {
  const fill = "absolute inset-0 w-full h-full object-cover cursor-pointer";

  const handleClick = (e) => {
    e.stopPropagation();
    onClick?.(media);
  };

  const isVideo = media.type === "video";

  return (
    <div
      style={style}
      className={`relative min-w-0 min-h-0 overflow-hidden ${
        isVideo ? "bg-black" : "bg-neutral-100 dark:bg-neutral-800"
      }`}
    >
      {isVideo ? (
        <AutoVideo
          src={media.url}
          className={fill}
          onClick={handleClick}
          onMeta={(ratio) => onRatio(media, ratio)}
          iconClass="w-14 h-14 text-3xl"
        />
      ) : (
        <img
          src={media.url}
          alt=""
          className={fill}
          onClick={handleClick}
          onLoad={(e) =>
            onRatio(
              media,
              e.currentTarget.naturalWidth / e.currentTarget.naturalHeight
            )
          }
        />
      )}

      {renderOverlay?.(index)}
    </div>
  );
}

/* ========================================
   AUTO VIDEO
   - Plays automatically (muted) while ~half of it is on screen,
     pauses when scrolled away.
   - 🔇 / 🔊 button lets the user mute / unmute.
   - Tapping the video itself still calls onClick (zoom view).
   Must be rendered inside a `relative` wrapper.
======================================== */

function AutoVideo({
  src,
  className,
  onClick,
  onMeta,
  iconClass = "w-14 h-14 text-3xl",
}) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);

  /* keep the real <video> element in sync with the mute state */
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  /* play when visible, pause when not */
  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {
            /* autoplay blocked — the ▶ icon stays visible */
          });
        } else {
          video.pause();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [src]);

  const toggleMute = (e) => {
    e.stopPropagation();
    setMuted((m) => !m);
  };

  return (
    <>
      <video
        ref={videoRef}
        src={src}
        muted={muted}
        loop
        playsInline
        preload="metadata"
        className={className}
        onClick={onClick}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedMetadata={(e) =>
          onMeta?.(e.currentTarget.videoWidth / e.currentTarget.videoHeight)
        }
      />

      {/* ▶ only while paused (e.g. autoplay blocked) */}
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            className={`${iconClass} rounded-full bg-black/60 flex items-center justify-center text-white`}
          >
            ▶
          </div>
        </div>
      )}

      {/* MUTE / UNMUTE */}
      <button
        type="button"
        onClick={toggleMute}
        aria-label={muted ? "Unmute video" : "Mute video"}
        className="absolute bottom-2 right-2 z-10 w-8 h-8 rounded-full bg-black/60 text-white text-sm flex items-center justify-center touch-manipulation active:opacity-70"
      >
        {muted ? "🔇" : "🔊"}
      </button>
    </>
  );
}